
/**
 * This is used for integration with Hilton's Fusion application
 * For testing, navigate to: /#/internal/connector
 * See also: MessagingModule, ConnectorModule
 */

import { filter } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Router, NavigationEnd, NavigationExtras } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from '../authentication/auth.service';

export interface WindowConnectorApi {
  receive(cb: unknown);
  send(msg: unknown);
}

export class WindowMessageData {
  meta: { [attribute: string]: unknown }; // eg. { externalAlertId: 'trackingId' }
  incident: Incident;
  facilityList: Facility[];
  action?: string;
}

export class WindowMessageNavigate {
  commands: unknown[];
  extras: NavigationExtras;
}

export class WindowMessage {
  type: string;
  action: string;
  navigate: WindowMessageNavigate; // REMOVE once we move to regular actions
  data: WindowMessageData;
}

export class WindowMessagePayload {
  data: WindowMessage;
}

// Properties are capitolized to match Attribute name and consistency for additional properties that are used for display
export class Facility {
  FacilityID: string;
  FacilityName: string; // Optional?
  FacilityCity: string; // Optional?
  FacilityCountry: string; // Optional?
}

export class Incident {
  externalId: string
}

const LAST_WINDOW_MESSAGE = 'last-window-message';

@Injectable()
export class WindowMessageService {
  lastMessage: BehaviorSubject<WindowMessage> = new BehaviorSubject<WindowMessage>(null);

  private pauseAccept: boolean = false;

  private get storedMessage(): WindowMessage {
    const storedMessage = localStorage.getItem(LAST_WINDOW_MESSAGE);
    return JSON.parse(storedMessage);
  }
  private set storedMessage(message: WindowMessage) {
    if (message) {
      localStorage.setItem(LAST_WINDOW_MESSAGE, JSON.stringify(message));
    } else {
      localStorage.removeItem(LAST_WINDOW_MESSAGE);
    }
  }

  constructor(protected router: Router, private authService: AuthService) {

    // Always save last message in case we need to reload (login workflow, leaving other alert page, etc.)
    this.lastMessage.subscribe(msg => this.storedMessage = msg);

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(_ => {
      this.pauseAccept = false;
    });

    this.authService.user.data.subscribe(user => {
      if (user && this.storedMessage) {
        // If login or loading for the first time, check if we saved something in local storage and re-apply it
          this.processMessage(this.storedMessage);
      }
    });
  }

  acceptMessage(message: WindowMessage) {
    if (this.pauseAccept) {
      setTimeout(() => this.acceptMessage(message), 10);
    } else if (this.lastMessage.value === message) {
      // Wait until all listeners get the message before clearing
      setTimeout(() => {
        this.lastMessage.next(null);
      }, 0);
    }
  }

  processMessage(message: WindowMessage) {

    // Perform known action
    if (message.action) {
      // Pause accept until we navigate, so existing components don't erase message
      this.pauseAccept = true;

      switch (message.action.toLowerCase()) {
        case 'sendalert':
          this.router.navigate(['/alerts/create']);
          break;
        case 'sendpoll':
          this.router.navigate(['/alerts/create/poll']);
          break;
        case 'testconnector':
          this.router.navigate(['/internal/connector/message-display']);
          break;
        default:
          this.pauseAccept = false;
          throw new Error('unsupported action "' + message.action + '"');
      }
    }

    // Store message for retrieval
    this.lastMessage.next(message);

    // REMOVE once we move to regular actions
    const nav = message.navigate;
    if (nav?.commands) {
      this.router.navigate(nav.commands, nav.extras);
    }
  }
}
